import React, { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Icon, IconNames, ToastTypes } from 'components/atoms';
import { Colors } from 'context';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { PATHS } from 'router/config';
import { AttachmentType } from 'components/types';
import { useGroupLessonAttachmentLazyQuery } from 'core/graphql';
import { useToaster } from 'core/hooks';
import type { ITextbookFile } from 'core';

interface ITextbookFileLink {
  file: ITextbookFile;
  className?: string;
}

/**
 * Ссылка на файл учебника из карточки ДЗ (ТЗ 0026).
 *
 * Ссылку на S3 сюда НЕ передают: presigned-URL живёт 20 секунд и к моменту
 * клика протухает. PDF открывается штатным просмотрщиком по id, всё
 * остальное — ленивым запросом с `network-only`, который минтит свежий URL
 * ровно в момент нажатия.
 */
export const TextbookFileLink = memo(({ file, className }: ITextbookFileLink) => {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const [fetchAttachment, { loading }] = useGroupLessonAttachmentLazyQuery({
    fetchPolicy: 'network-only',
  });

  const onOpenOther = useCallback(async () => {
    // Вкладку открываем СИНХРОННО внутри жеста — иначе блокировщик всплывающих
    // окон убьёт её, пока идёт запрос (тот же приём, что в отдаче сдач ДЗ).
    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
    }
    try {
      const { data } = await fetchAttachment({
        variables: { attachmentId: file.id, attachmentRole: AttachmentType.student },
      });
      const url = data?.groupLessonAttachment?.file;
      if (url && tab) {
        tab.location.href = url;
        return;
      }
      tab?.close();
      addToast('textbook_open', {
        type: ToastTypes.danger,
        text: `${t('homework.textbook.openError')}`,
      });
    } catch {
      tab?.close();
      addToast('textbook_open', {
        type: ToastTypes.danger,
        text: `${t('homework.textbook.openError')}`,
      });
    }
  }, [addToast, fetchAttachment, file.id, t]);

  return (
    <Row className={className}>
      <FileIcon name={IconNames.Note} />
      <FileName>{file.fileName || file.title}</FileName>
      {file.isPdf ? (
        <OpenLink to={`${PATHS.attachments}/${AttachmentType.student}/${file.id}`} target="_blank">
          {t('homework.textbook.open')}
        </OpenLink>
      ) : (
        <OpenButton type="button" onClick={onOpenOther} disabled={loading}>
          {t('homework.textbook.open')}
        </OpenButton>
      )}
    </Row>
  );
});

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const FileIcon = styled((props) => <Icon {...props} />)`
  width: 16px;
  height: 16px;
  flex: none;
`;

const FileName = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  word-break: break-all;
`;

const OpenLink = styled(Link)`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
  color: ${Colors.black};
  text-decoration: underline;
  white-space: nowrap;
`;

const OpenButton = styled.button`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
  padding: 0;
  border: none;
  background: none;
  color: ${Colors.black};
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    color: ${Colors.gray};
    cursor: default;
  }
`;
